"use client";

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Search, CircleQuestionMark as HelpCircle, CircleCheck as CheckCircle2, MapPin, PhoneCall, ArrowRight, MessageSquare, Sparkles } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "faq-1",
  category: "Strategy & GBP",
  question: "Why should my Preston business focus on local SEO instead of general national SEO?",
  answer: "If your target clients live or work in Preston, Central Lancashire, or surrounding areas like Fulwood, Penwortham, and Bambers Bridge, national SEO casts too wide a net. Local SEO specifically targets high-intent searchers seeking immediate local solutions. By optimizing your Google Business Profile, geographically relevant content, and local citations, I help you dominate the local Google Map Pack and localized search results where conversion rates are significantly higher."
}, {
  id: "faq-2",
  category: "Working Direct",
  question: "Will I be working directly with you, Yassine, or handed over to an account manager?",
  answer: "You work 100% directly with me. As an independent freelance SEO consultant, I do not have junior account executives, middle managers, or outsourced agencies handling campaigns behind the scenes. When you book a call, discuss strategy, or review performance reports, you are speaking directly to the specialist executing the strategy, technical audits, and optimization on your account."
}, {
  id: "faq-3",
  category: "Rankings & Timeline",
  question: "How long does it take to see tangible local SEO results in Preston and Lancashire?",
  answer: "Most Preston businesses start seeing measurable movement in local Google Map Packs and localized organic keywords within 8 to 12 weeks. Highly competitive sectors across Central Lancashire may take 4 to 6 months to secure top 3 positions. I provide transparent reporting from day one so you can track ranking progress, map views, phone calls, and web form submissions."
}, {
  id: "faq-4",
  category: "Strategy & GBP",
  question: "How do you optimize my Google Business Profile (GBP) for Preston searchers?",
  answer: "Google Business Profile optimization is the cornerstone of local visibility. I refine your primary and secondary categories, configure local service area targeting across PR postcodes, structure geo-tagged photo assets, establish a structured review acquisition strategy, and regularly post updates aligned with what Preston locals actively search for."
}, {
  id: "faq-5",
  category: "Strategy & GBP",
  question: "Can local SEO help my Preston business rank in nearby towns like Fulwood, Leyland, and Chorley?",
  answer: "Yes, absolutely. Preston functions as the primary commercial hub for Central Lancashire. I construct dedicated local landing pages optimized specifically for key surrounding locations—such as Chorley, Leyland, Penwortham, Fulwood, and Kirkham—without creating thin or duplicate content. This expands your reach across the broader PR postcode zone."
}, {
  id: "faq-6",
  category: "Working Direct",
  question: "What makes your freelance local SEO consultancy different from a typical Lancashire agency?",
  answer: "Agencies often lock you into long, rigid 12-month retainers with substantial overhead costs and pass your project down to junior staff. With me, you receive senior-level SEO expertise, direct communication via email or call, tailored strategy, and total transparency. Every recommendation I make is aimed directly at your revenue and lead growth."
}, {
  id: "faq-7",
  category: "Strategy & GBP",
  question: "How do you handle local link building in Preston without using risky directories?",
  answer: "I strictly avoid low-quality directory spam or automated link schemes that risk Google penalties. Instead, I earn authentic local authority through targeted outreach to respected Lancashire business publications, local sponsorships, community partnerships, industry guest features, and verified UK business directories."
}, {
  id: "faq-8",
  category: "Strategy & GBP",
  question: "Is local SEO relevant for B2B firms operating out of Preston business parks or Winckley Square?",
  answer: "Yes. B2B buyers search locally first when looking for trusted partners, legal advisors, IT support, logistics, or corporate consultants. Optimizing your Preston entity signals to both local decision-makers and regional clients that you are an established, authoritative business with a physical footprint and verified local reputation."
}, {
  id: "faq-9",
  category: "AI & Modern SEO",
  question: "How does AI Search (Google AI Overviews & ChatGPT) impact local Preston searches?",
  answer: "Search is evolving into AI Search Optimisation (AISO). When Preston residents query AI search assistants for local recommendations, these engines rely on structured entity data, consistent NAP (Name, Address, Phone), real customer reviews, and authoritative local content. I structure your presence so both traditional Google search and AI models confidently recommend your business."
}, {
  id: "faq-10",
  category: "Rankings & Timeline",
  question: "Do I need a complete website redesign before starting a Preston local SEO campaign?",
  answer: "Rarely. In most cases, I optimize your current website through targeted technical fixes, speed enhancements, localized content structuring, and strategic call-to-action improvements. If your current website is severely outdated or broken, I will give you honest advice on cost-effective options rather than pushing an unnecessary total rebuild."
}, {
  id: "faq-11",
  category: "Rankings & Timeline",
  question: "How do you track local rankings, map pack positions, and incoming leads in Preston?",
  answer: "I measure performance against actual business outcomes: top 3 Map Pack rankings, organic search visits from Lancashire, phone calls initiated from Google Maps, and completed contact forms. You receive a concise monthly report directly from me detailing work completed, real visibility gains, and upcoming priorities."
}, {
  id: "faq-12",
  category: "Working Direct",
  question: "What happens during our initial free local SEO strategy consultation?",
  answer: "We hold a 30-minute 1-on-1 call where I analyze your current search visibility in Preston, pinpoint immediate quick-win opportunities, inspect local competitor rankings, and discuss your business goals. There is no sales pitch or obligation—just clear, actionable insight so you can decide if working directly with me fits your needs."
}];
const categories = ["All Questions", "Strategy & GBP", "Working Direct", "Rankings & Timeline", "AI & Modern SEO"];
export default function PrestonFaq() {
  const [activeCategory, setActiveCategory] = useState("All Questions");
  const [searchQuery, setSearchQuery] = useState("");
  const shouldReduceMotion = useReducedMotion();
  const filteredFaqs = useMemo(() => {
    return faqData.filter(item => {
      const matchesCategory = activeCategory === "All Questions" || item.category === activeCategory;
      const matchesQuery = searchQuery.trim() === "" || item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, searchQuery]);
  return <section id="prestonlocalseofaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/40 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-foreground text-xs sm:text-sm font-medium mb-4 border border-border">
            <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
            <span>Preston & Central Lancashire Local SEO</span>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight mb-4">
            Frequently Asked Local SEO Questions in Preston
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Clear, honest answers about ranking your business in Preston and surrounding postcodes. 
            No agency jargon—just straightforward search advice directly from me, Yassine Essaadi.
          </motion.p>
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.25
      }} className="mb-12 p-6 sm:p-8 bg-card rounded-2xl border border-border shadow-xs flex flex-col md:flex-row items-center gap-6">
          <div className="shrink-0 relative">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_portrait.png-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant Preston" loading="lazy" decoding="async" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-primary shadow-sm" />
            <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground p-1.5 rounded-full shadow-xs">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-lg font-serif font-semibold text-foreground mb-1">
              {"You work directly with me — Yassine Essaadi"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {"I manage your Preston SEO strategy end-to-end. I am always accessible to answer your questions, refine your strategy, and ensure your local business gets the search visibility it deserves."}
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/LocalSEOPage">
              <Button variant="outline" size="sm" className="gap-2 border-primary/20 hover:border-primary text-primary font-medium">
                <span>Explore Local SEO Services</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.div>

        <div className="mb-8 space-y-4">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search local SEO questions (e.g. Google Maps, Leyland, pricing)..." className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-2xs" />
            {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground h-7 px-2">
                Clear
              </Button>}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {categories.map((cat, i) => {
            const isActive = activeCategory === cat;
            return <div data-index={i} key={cat}>
                  <Button variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(cat)} className={`rounded-full text-xs sm:text-sm font-medium transition-all ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "bg-background text-foreground border-border hover:bg-muted"}`}>
                    {cat}
                  </Button>
                </div>;
          })}
          </div>
        </div>

        <div className="max-w-4xl mx-auto bg-card rounded-2xl border border-border p-4 sm:p-8 shadow-xs">
          {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-3">
              {filteredFaqs.map((item, i) => <div data-index={i} key={item.id}>
                  <AccordionItem value={item.id} className="border border-border/60 rounded-xl px-4 sm:px-6 py-1 bg-background hover:border-primary/30 transition-colors shadow-2xs">
                    <AccordionTrigger className="text-left font-serif font-semibold text-base sm:text-lg text-primary hover:no-underline hover:text-primary/80 transition-colors py-4">
                      <span className="pr-2">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 font-default border-t border-border/40 mt-1">
                      <div className="pt-3">
                        {item.answer}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion> : <div className="text-center py-12 px-4">
              <HelpCircle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <h3 className="text-lg font-serif font-semibold text-foreground mb-1">
                No matching questions found
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {"Try searching with different terms or select 'All Questions'."}
              </p>
              <Button variant="outline" size="sm" onClick={() => {
            setSearchQuery("");
            setActiveCategory("All Questions");
          }}>
                Reset Filters
              </Button>
            </div>}
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-12 md:mt-16 max-w-4xl mx-auto rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 text-center relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          
          <div className="relative z-10 max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-medium mb-4 backdrop-blur-xs">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Have a Specific Local SEO Question?</span>
            </span>

            <h3 className="text-2xl sm:text-3xl font-serif font-bold mb-3 tracking-tight">
              Let us Discuss Your Preston Market Search Potential
            </h3>

            <p className="text-primary-foreground/90 text-sm sm:text-base mb-8 leading-relaxed font-default">
              Book a free 30-minute local SEO strategy call directly with me. No pushy sales pitch—just real insights on how to rank higher across Central Lancashire.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/ContactPage">
                <Button size="lg" className="bg-background text-foreground hover:bg-background/90 font-semibold px-6 shadow-md w-full sm:w-auto">
                  <PhoneCall className="w-4 h-4 mr-2 text-primary" />
                  <span>Book a Free SEO Consultation</span>
                </Button>
              </Link>
              
              <Link to="/CaseStudiesPage">
                <Button variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium w-full sm:w-auto">
                  <span>Explore My Case Studies</span>
                </Button>
              </Link>
            </div>

            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-primary-foreground/80">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                <span>100% Direct Consultant Access</span>
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                <span>No Agency Lock-In</span>
              </span>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}